import React, { useEffect, useMemo, useState } from "react";

import { Close } from "@mui/icons-material";
import UndoIcon from "@mui/icons-material/Undo";
import { Button, Fade, IconButton, Stack, TextField, Typography } from "@mui/material";
import { BinDeleteIcon } from "assets/material-custom-icons";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { ValidationError } from "components/Validation-error/ValidationError";
import { useActionHook } from "hooks";
import { debounce, uniqueId } from "lodash";
import { useDealActions } from "store";

interface IRoundsData {
  id: number | string;
  name: string;
  is_deleted: boolean;
}

interface IProps {
  dealId: number | null;
  onClose: () => void;
}

export const Content: React.FC<IProps> = ({ dealId, onClose }) => {
  const [state, setState] = useState<IRoundsData[]>([]);
  const [errors, setErrors] = useState<Record<number | string, string[]>>({});
  const [errorFromApi, setErrorFromApi] = useState<Record<number | string, string[]>>({});
  const [isLoading, setIsLoading] = useState(false);
  const { getRoundsAction, manageDealRoundsAction, getDealRoundsWithInvestorsAndStoreAction } = useDealActions();
  const { action: getDealRounds } = useActionHook(getRoundsAction, {
    onSuccess: (data) => {
      const rounds = data.map((round) => ({
        id: round.id,
        name: round.name,
        is_deleted: false
      }));
      setState(rounds);
    },
    onFinally: () => {
      setIsLoading(false);
    }
  });

  const roundsNamesError: string = useMemo(() => {
    let res = "";

    const roundsNames = state.filter((item) => !item.is_deleted).map((item) => item.name);
    const uniqueNamesArray = Array.from(new Set(roundsNames));

    if (roundsNames.length !== uniqueNamesArray.length) {
      res = "There are two or more rounds with the same name.";
    }

    return res;
  }, [state]);

  const hasErrors = useMemo<boolean>(() => {
    return Boolean(Object.entries(errors).length || Object.entries(errorFromApi).length);
  }, [errors, errorFromApi]);

  const clearError = (id: string | number) => {
    if (!errors[id]) return;
    setErrors((prevState) => {
      const errorsCopy = { ...prevState };

      delete errorsCopy[id];

      return errorsCopy;
    });
  };

  const handleChangeName = debounce((id: string | number, newValue: string) => {
    setState((prevState) =>
      prevState.map((round) => {
        if (round.id !== id) return round;
        clearError(id);
        return { ...round, name: newValue };
      })
    );
  }, 500);

  const handleIsDeleted = ({ id, is_deleted }: { id: number | string; is_deleted: boolean }) => {
    clearError(id);
    const errorFromApiCopy = { ...errorFromApi };
    delete errorFromApiCopy[id];
    setErrorFromApi(errorFromApiCopy);

    setState((prevState) => prevState.map((round) => (round.id === id ? { ...round, is_deleted } : round)));
  };

  const handleAddRound = () => {
    setState((prevState) => [...prevState, { id: uniqueId("").toString(), name: "New round", is_deleted: false }]);
  };

  const memoState = useMemo(() => {
    return state.filter((item) => !item.is_deleted);
  }, [state]);

  const validate = (state: IRoundsData[]): void => {
    const errors = state.reduce<Record<number | string, string[]>>((acc, round) => {
      return {
        ...acc,
        ...(!round.is_deleted && !round.name && { [round.id]: ["Round name is required"] })
      };
    }, {});

    if (Object.entries(errors).length) {
      setErrors(errors);
      throw "Validation failed";
    }
  };

  const errorFromApiHandler = (e: { errors: Record<string, Record<string, string[]>> }) => {
    const result: Record<string, string[]> = {};

    Object.values(e.errors).forEach((innerObj) => {
      Object.entries(innerObj).forEach(([key, value]) => {
        result[key] = value;
      });
    });

    setErrorFromApi(result);
  };

  const handleSubmit = async () => {
    if (!dealId) return;
    try {
      validate(state);
      await manageDealRoundsAction({
        [dealId]: state.filter((item) => typeof item.id === "number" || !item.is_deleted)
      });
      await getDealRoundsWithInvestorsAndStoreAction(dealId);
      await getRoundsAction({ dealId, with_commitments: true, dispatch: true });
      onClose();
    } catch (e) {
      errorFromApiHandler(e as { errors: Record<string, Record<string, string[]>> });
    }
  };

  useEffect(() => {
    if (dealId) {
      setIsLoading(true);
      getDealRounds({ dealId });
    }
  }, [dealId]);

  return (
    <Stack
      sx={{
        ...style,
        width: 457,
        gap: "20px",
        p: "10px 30px 30px 30px",
        borderRadius: "8px",
        boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
        bgcolor: "white"
      }}
    >
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          paddingY: "7px",
          borderBottom: "1px solid #DAE0E2",
          alignItems: "center"
        }}
      >
        <Typography sx={{ color: "#0A3142", fontSize: "16px", fontWeight: 500 }}>Rounds management</Typography>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </Stack>
      <AsyncContentMui isLoading={isLoading}>
        <Stack
          sx={{
            gap: "15px",
            minHeight: "100px",
            maxHeight: 400,
            overflow: "auto",
            p: "5px 0"
          }}
        >
          {state.length ? (
            state.map(({ id, name, is_deleted }, index) => (
              <Fade in key={id} timeout={(index + 1) * 200}>
                <Stack direction="row" sx={{ gap: "10px" }}>
                  <TextField
                    onChange={(e) => handleChangeName(id, e.target.value.trim())}
                    defaultValue={name}
                    fullWidth
                    disabled={is_deleted}
                    label="Round Name"
                    {...(is_deleted && { sx: { input: { textDecoration: "line-through" } } })}
                    error={Boolean(errors[id] || errorFromApi[id])}
                    helperText={errors[id]?.[0] || errorFromApi[id]?.[0]}
                  />
                  <MaterialTooltip title={is_deleted ? "Restore" : "Delete"} isShow placement="right">
                    <IconButton
                      sx={{ transform: "translate(0, 4px)" }}
                      disabled={!is_deleted && memoState.length < 2}
                      onClick={() => handleIsDeleted({ id: id, is_deleted: !is_deleted })}
                    >
                      {is_deleted ? <UndoIcon /> : <BinDeleteIcon />}
                    </IconButton>
                  </MaterialTooltip>
                </Stack>
              </Fade>
            ))
          ) : (
            <Typography>No rounds were found</Typography>
          )}
        </Stack>
        {roundsNamesError && <ValidationError messages={[roundsNamesError]} />}
        <Button
          sx={{
            alignSelf: "flex-start",
            fontSize: "15px",
            fontWeight: 400,
            textDecoration: "underline",
            ":hover": { textDecoration: "underline" }
          }}
          onClick={handleAddRound}
        >
          Add Round
        </Button>

        <Stack direction="row" sx={{ justifyContent: "flex-end", alignItems: "center", gap: "18px" }}>
          <Fade in timeout={300}>
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
          </Fade>
          <Fade in timeout={400}>
            <Button variant="contained" onClick={handleSubmit} disabled={Boolean(roundsNamesError) || hasErrors}>
              Submit
            </Button>
          </Fade>
        </Stack>
      </AsyncContentMui>
    </Stack>
  );
};

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  outline: "none"
};
